<template>
  <div class="center-bar" :class="[`center-bar--${mode}`]" role="timer" :aria-label="ariaLabel">
    <div class="center-bar__track" aria-hidden="true">
      <span class="center-bar__fill" :style="{ width: `${fillPercent}%` }"></span>
    </div>
    <div class="center-bar__dial">
      <span class="center-bar__halo" aria-hidden="true"></span>
      <svg class="center-bar__ring" viewBox="0 0 100 100" aria-hidden="true">
        <circle class="center-bar__ring-bg" cx="50" cy="50" :r="R" />
        <circle
          class="center-bar__ring-arc"
          cx="50"
          cy="50"
          :r="R"
          :stroke-dasharray="CIRCUMFERENCE"
          :stroke-dashoffset="dashOffset"
          transform="rotate(-90 50 50)"
        />
      </svg>
      <Transition name="center-bar-tick" mode="out-in">
        <span :key="value" class="center-bar__value cb-num">{{ value }}</span>
      </Transition>
    </div>
  </div>
</template>

<script setup>
/**
 * GameCenterBar — центральная панель экрана игры (экран 5): светящийся горизонтальный трек
 * и круглый циферблат с обратным отсчётом (калибровка) или таймером мм:сс (игра).
 *
 * <GameCenterBar mode="countdown" value="5" :progress="0.2" :ring="0.8" />
 * <GameCenterBar mode="timer" value="01:15" :progress="1" :ring="0.25" />
 *
 * props: mode ('countdown'|'timer'|'idle'), value (текст в круге), progress (заполнение трека 0..1),
 *        ring (дуга вокруг круга 0..1).
 */
import { computed } from "vue";

const props = defineProps({
  mode: { type: String, default: "countdown", validator: (v) => ["countdown", "timer", "idle"].includes(v) },
  value: { type: [String, Number], default: "" },
  progress: { type: Number, default: 0 },
  ring: { type: Number, default: 1 },
});

const R = 44;
const CIRCUMFERENCE = 2 * Math.PI * R;

const clamp01 = (v) => Math.min(1, Math.max(0, Number(v) || 0));
const fillPercent = computed(() => clamp01(props.progress) * 100);
const dashOffset = computed(() => (CIRCUMFERENCE * (1 - clamp01(props.ring))).toFixed(2));
const ariaLabel = computed(() => (props.mode === "timer" ? `Время игры ${props.value}` : `Отсчёт ${props.value}`));
</script>

<style scoped>
.center-bar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 96px;
  flex-shrink: 0;
}

.center-bar__track {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 26px;
  transform: translateY(-50%);
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.35) 0%, rgba(255, 255, 255, 0.04) 100%), var(--cb-surface);
  border: 1px solid var(--cb-border);
  box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.55), 0 0 0 4px rgba(221, 245, 90, 0.04);
  overflow: hidden;
}

.center-bar__fill {
  position: absolute;
  top: 4px;
  bottom: 4px;
  left: 4px;
  max-width: calc(100% - 8px);
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(221, 245, 90, 0.35) 0%, var(--cb-lime) 70%, #f2fa8c 100%);
  box-shadow: 0 0 14px rgba(221, 245, 90, 0.65);
  transition: width 0.12s linear;
}

.center-bar--timer .center-bar__fill {
  background: linear-gradient(90deg, rgba(221, 245, 90, 0.15) 0%, rgba(221, 245, 90, 0.55) 45%, rgba(221, 245, 90, 0.55) 55%, rgba(221, 245, 90, 0.15) 100%);
  box-shadow: 0 0 10px rgba(221, 245, 90, 0.35);
}

.center-bar--idle .center-bar__fill {
  opacity: 0.25;
}

.center-bar__dial {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 92px;
  height: 92px;
  border-radius: 50%;
  background: radial-gradient(circle at 50% 35%, #1d2419 0%, #0e120c 75%);
  box-shadow: 0 0 0 6px rgba(11, 14, 10, 0.85), 0 0 0 7px rgba(221, 245, 90, 0.18), 0 8px 24px rgba(0, 0, 0, 0.6);
}

.center-bar__halo {
  position: absolute;
  inset: -28px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(221, 245, 90, 0.18) 0%, rgba(221, 245, 90, 0.06) 45%, rgba(221, 245, 90, 0) 70%);
  pointer-events: none;
  animation: cb-glow-breathe 2.8s ease-in-out infinite;
}

.center-bar__ring {
  position: absolute;
  inset: 4px;
  width: calc(100% - 8px);
  height: calc(100% - 8px);
  overflow: visible;
}

.center-bar__ring-bg {
  fill: none;
  stroke: rgba(221, 245, 90, 0.16);
  stroke-width: 6;
}

.center-bar__ring-arc {
  fill: none;
  stroke: var(--cb-lime);
  stroke-width: 6;
  stroke-linecap: round;
  filter: drop-shadow(0 0 4px rgba(221, 245, 90, 0.8));
  transition: stroke-dashoffset 0.2s linear;
}

.center-bar__value {
  position: relative;
  font-size: 44px;
  font-weight: 800;
  line-height: 1;
  color: var(--cb-text);
  text-shadow: 0 0 16px rgba(221, 245, 90, 0.35);
}

.center-bar--timer .center-bar__value {
  font-size: 21px;
  letter-spacing: 0.01em;
}

.center-bar-tick-enter-active,
.center-bar-tick-leave-active {
  transition: opacity 0.15s var(--cb-ease), transform 0.15s var(--cb-ease);
}

.center-bar--countdown .center-bar-tick-enter-from {
  opacity: 0;
  transform: scale(1.4);
}

.center-bar--countdown .center-bar-tick-leave-to {
  opacity: 0;
  transform: scale(0.7);
}

.center-bar--timer .center-bar-tick-enter-active,
.center-bar--timer .center-bar-tick-leave-active {
  transition: none;
}

@media (prefers-reduced-motion: reduce) {
  .center-bar__halo {
    animation: none;
  }
}
</style>
